import React, { useState } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { motion } from "framer-motion";
import {
  Settings as SettingsIcon,
  Store,
  Shield,
  User,
  Bell,
  Lock,
  Database,
  Globe,
  CreditCard,
  Activity,
} from "lucide-react";

export default function Settings() {
  const [form, setForm] = useState({
    storeName: "Shruti Eye Care",
    phone: "",
    email: "",
    address: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const saveSettings = () => {
    alert("System Settings Updated ✅");
  };

  const settingsModules = [
    {
      title: "Store Configuration",
      icon: Store,
      color: "from-green-500 to-emerald-600",
      desc: "Business identity & store profile settings",
    },
    {
      title: "Account Control",
      icon: User,
      color: "from-blue-500 to-cyan-600",
      desc: "Admin profile & user access control",
    },
    {
      title: "Security Center",
      icon: Shield,
      color: "from-red-500 to-pink-600",
      desc: "Authentication & permission management",
    },
    {
      title: "Notifications",
      icon: Bell,
      color: "from-yellow-500 to-orange-500",
      desc: "Alerts, SMS & system notifications",
    },
    {
      title: "Database",
      icon: Database,
      color: "from-purple-500 to-indigo-600",
      desc: "Backup, restore & data management",
    },
    {
      title: "Integrations",
      icon: Globe,
      color: "from-sky-500 to-blue-600",
      desc: "Payments, APIs & external tools",
    },
    {
      title: "Billing",
      icon: CreditCard,
      color: "from-pink-500 to-rose-600",
      desc: "Subscription & invoice settings",
    },
    {
      title: "System Logs",
      icon: Activity,
      color: "from-slate-500 to-slate-700",
      desc: "Activity tracking & audit logs",
    },
  ];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">

      {/* LEFT SIDEBAR */}
      <div className="fixed left-0 top-0 h-screen w-72 border-r border-white/10">
        <StoreSidebar />
      </div>

      {/* MAIN */}
      <div className="ml-72 flex-1 p-6 lg:p-8">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold flex items-center gap-2">
            <SettingsIcon size={28} />
            System Control Center
          </h1>

          <p className="text-slate-400 mt-2">
            Full ERP configuration, security & business management system
          </p>
        </motion.div>

        {/* MODULE GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">

          {settingsModules.map((item, i) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05, y: -5 }}
                className={`relative overflow-hidden p-6 rounded-3xl bg-gradient-to-r ${item.color}`}
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full" />

                <Icon size={26} />

                <h3 className="text-lg font-bold mt-3">
                  {item.title}
                </h3>

                <p className="text-white/80 text-sm mt-1">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}

        </div>

        {/* CONFIGURATION PANEL */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* STORE SETTINGS */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white/5 border border-white/10 rounded-3xl p-6"
          >
            <div className="flex items-center gap-2 mb-4 text-green-400">
              <Store size={18} />
              <h2 className="text-xl font-bold">Store Configuration</h2>
            </div>

            <input
              name="storeName"
              value={form.storeName}
              onChange={handleChange}
              placeholder="Store Name"
              className="w-full mb-3 p-3 rounded-xl bg-slate-900 border border-slate-800"
            />

            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone"
              className="w-full mb-3 p-3 rounded-xl bg-slate-900 border border-slate-800"
            />

            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
              className="w-full mb-3 p-3 rounded-xl bg-slate-900 border border-slate-800"
            />

            <input
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Store Address"
              className="w-full mb-4 p-3 rounded-xl bg-slate-900 border border-slate-800"
            />

            <button
              onClick={saveSettings}
              className="w-full bg-green-600 hover:bg-green-700 p-3 rounded-xl font-semibold"
            >
              Save Store Settings
            </button>
          </motion.div>

          {/* SECURITY PANEL */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white/5 border border-white/10 rounded-3xl p-6"
          >
            <div className="flex items-center gap-2 mb-4 text-red-400">
              <Shield size={18} />
              <h2 className="text-xl font-bold">Security & Access Control</h2>
            </div>

            <div className="space-y-3">

              <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center gap-2">
                <Lock size={16} /> Two Factor Authentication
              </div>

              <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center gap-2">
                <User size={16} /> Role-Based Permissions
              </div>

              <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center gap-2">
                <Activity size={16} /> Login Activity Tracking
              </div>

              <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center gap-2">
                <Database size={16} /> Secure Data Encryption
              </div>

            </div>
          </motion.div>

        </div>

        {/* SYSTEM FOOTER INSIGHT */}
        <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="font-bold mb-2">
            🧠 System Intelligence Layer
          </h3>

          <p className="text-slate-400 text-sm">
            This settings module acts as the control hub for your entire ERP system.
            Future upgrades can include AI-driven automation, auto-backups,
            predictive security alerts, and multi-branch control.
          </p>
        </div>

      </div>
    </div>
  );
}